import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { Check, ExternalLink } from "lucide-react";
import cablePlmLogo from "@/assets/cableplm-logo.svg.asset.json";
import cablePlmLogoOnDark from "@/assets/cableplm-logo-on-dark.svg.asset.json";

import { addonIcon } from "@/components/site/AddonCard";
import { GhostCta, PrimaryCta } from "@/components/site/Cta";
import { IsoIllustration, IsoPlate } from "@/components/site/Iso";
import { AddonHowItWorks } from "@/components/site/AddonHowItWorks";

import { Markdown } from "@/components/site/Markdown";
import { NotFoundBlock } from "@/components/site/NotFoundBlock";
import { Reveal } from "@/components/site/Reveal";
import { Section, SectionHeading } from "@/components/site/Section";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { getAddon } from "@/lib/pricing.functions";
import { addonIso } from "@/content/illustrations";
import { alternateLinks, localePath, normalizeLocale } from "@/lib/i18n";
import { useT } from "@/lib/i18n-dict";
import { SHOW_PRICES } from "@/lib/pricing-visibility";

export const Route = createFileRoute("/{-$locale}/addons/$slug")({
  staticData: { sitemap: true },
  loader: async ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    if (params.slug === "cableplm") {
      throw redirect({ href: localePath(locale, "/addons/cable-plm"), statusCode: 301 });
    }
    const detail = await getAddon({ data: { slug: params.slug, locale } });
    if (!detail) throw notFound();
    return detail;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Add-on unavailable | factory.online" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { addon } = loaderData;
    const title = `${addon.name} add-on | factory.online`;
    const description = addon.description || addon.tagline;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: alternateLinks(`/addons/${addon.slug}`),
      scripts: addon.faqs.length
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: addon.faqs.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: { "@type": "Answer", text: faq.answer },
                })),
              }),
            },
          ]
        : [],
    };
  },

  notFoundComponent: () => (
    <NotFoundBlock
      title="Add-on not found"
      lead="This add-on may have been renamed or unpublished."
      backTo="/addons"
      backLabel="All add-ons"
    />
  ),
  component: AddonDetailPage,
});

function AddonDetailPage() {
  const { addon, planNames } = Route.useLoaderData();
  const t = useT();
  const Icon = addonIcon(addon.icon);
  const art = addonIso(addon.slug);

  return (
    <>
      {/* Hero */}
      <header className="hero-glow border-b border-border/60 py-20 md:py-24">
        <div className="container-page grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">Add-on</p>
            <h1 className="mt-4 font-display text-4xl font-bold text-balance-tight md:text-5xl">
              {addon.name}
            </h1>
            {addon.tagline ? (
              <p className="mt-4 text-lg text-muted-foreground">{addon.tagline}</p>
            ) : null}
            <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
              {addon.description}
            </p>
            {addon.slug === "cable-plm" || addon.slug === "cableplm" ? (
              <a
                href="https://www.cableplm.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex max-w-full flex-wrap items-center gap-x-5 gap-y-3 rounded-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background"
              >
                <img
                  src={cablePlmLogo.url}
                  alt="CablePLM"
                  width={1445}
                  height={291}
                  className="block h-8 w-auto max-w-full object-contain dark:hidden"
                />
                <img
                  src={cablePlmLogoOnDark.url}
                  alt="CablePLM"
                  width={1445}
                  height={291}
                  className="hidden h-8 w-auto max-w-full object-contain dark:block"
                />
                <span className="inline-flex items-center gap-2 text-base">
                  www.cableplm.com
                  <ExternalLink className="size-4 shrink-0" aria-hidden />
                </span>
              </a>
            ) : null}
            <div className="mt-8 flex flex-wrap items-baseline gap-3">
              <span className="font-display text-3xl font-bold">
                {SHOW_PRICES ? addon.price : t("On request")}
              </span>
              {SHOW_PRICES && addon.period ? (
                <span className="text-sm text-muted-foreground">{addon.period}</span>
              ) : null}
            </div>
            {planNames.length ? (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">{t("Available for")}</span>
                {planNames.map((name) => (
                  <span
                    key={name}
                    className="rounded-full border border-border px-2.5 py-0.5 text-xs leading-relaxed tracking-wide text-muted-foreground"
                  >
                    {name}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="mt-8 flex flex-wrap gap-3">
              <PrimaryCta to="/registration-demo">Get a demo</PrimaryCta>
              <GhostCta to="/prices">See plans</GhostCta>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <IsoIllustration
              name={art}
              alt={`${addon.name} shown as a schematic isometric scene`}
              className="mb-8"
            />

            <IsoPlate className="rounded-2xl border border-border bg-surface p-8">
              <Icon className="size-10 text-primary" aria-hidden />
              <p className="mt-6 font-display text-sm font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                What you get
              </p>
              <ul className="mt-4 space-y-3">
                {addon.highlights.slice(0, 6).map((h) => (
                  <li key={h} className="flex gap-3 text-base text-muted-foreground">
                    <Check className="mt-1 size-5 shrink-0 text-primary" aria-hidden />
                    {h}
                  </li>
                ))}
              </ul>
            </IsoPlate>
          </Reveal>
        </div>
      </header>

      {/* How it works */}
      <AddonHowItWorks
        slug={addon.slug}
        name={addon.name}
        steps={addon.steps}
        lead={addon.howLead}
      />

      {/* Capability list */}

      {addon.highlights.length > 6 ? (
        <Section>
          <SectionHeading eyebrow={t("Capabilities")} title={t("Everything inside {name}").replace("{name}", addon.name)} />
          <ul className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {addon.highlights.map((h, index) => (
              <Reveal
                key={h}
                delay={index * 30}
                className="flex gap-3 rounded-xl border border-border bg-surface/40 p-5 text-base text-muted-foreground"
              >
                <Check className="mt-1 size-5 shrink-0 text-primary" aria-hidden />
                {h}
              </Reveal>
            ))}
          </ul>
        </Section>
      ) : null}

      {/* Long-form body */}
      {addon.body ? (
        <Section className="bg-surface/40">
          <Reveal className="max-w-3xl">
            <Markdown source={addon.body} />
          </Reveal>
        </Section>
      ) : null}

      {/* Tiers */}
      {addon.tiers.length ? (
        <Section>
          <SectionHeading
            eyebrow="Editions"
            title={`${addon.name} editions`}
            lead="Pick the edition that matches how deep you need to go."
          />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {addon.tiers.map((tier, index) => (
              <Reveal
                key={tier.id}
                delay={index * 40}
                className="flex h-full flex-col rounded-xl border border-border bg-surface/40 p-6"
              >
                <h3 className="font-display text-lg font-bold">{tier.name}</h3>
                {tier.blurb ? (
                  <p className="mt-1 text-sm text-muted-foreground">{tier.blurb}</p>
                ) : null}
                <p className="mt-5">
                  <span className="font-display text-2xl font-bold">
                    {SHOW_PRICES ? tier.price : t("On request")}
                  </span>
                  {SHOW_PRICES && tier.period ? (
                    <span className="text-sm text-muted-foreground"> {tier.period}</span>
                  ) : null}
                </p>
                <ul className="mt-6 flex-1 space-y-2.5">
                  {tier.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-sm text-muted-foreground">
                      <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                      {f}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </Section>
      ) : null}

      {/* FAQ */}
      {addon.faqs.length ? (
        <Section id="faq">
          <SectionHeading
            eyebrow="FAQ"
            title={`${addon.name} — ${t("frequent questions")}`}
            lead="Short answers to what teams ask before switching this module on."
          />
          <Reveal className="mt-10 max-w-3xl">
            <Accordion type="single" collapsible className="w-full">
              {addon.faqs.map((faq, index) => (
                <AccordionItem key={faq.id} value={`faq-${index}`}>
                  <AccordionTrigger className="text-start font-display text-base">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </Section>
      ) : null}

      {/* CTA */}
      <Section className="bg-surface/40">
        <Reveal className="rounded-2xl border border-primary/30 bg-surface p-10 text-center shadow-[var(--glow-primary)] md:p-16">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-balance-tight md:text-4xl">
            {t("See {name} running on your data").replace("{name}", addon.name)}
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryCta to="/registration-demo">Get a demo</PrimaryCta>
            <GhostCta to="/addons">All add-ons</GhostCta>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
